import { appIntl } from '@components/locale/IntlGlobalProvider';
import { createToast, ToastStatusEnum } from '@components/ui/toaster';
import { BaseQueryFn } from '@reduxjs/toolkit/query';
import { TypedMutationTrigger } from '@reduxjs/toolkit/query/react';
import { createErrorMessage } from '@utils/common.utils';
import { ChangeEvent } from 'react';

import { DownloadToastTypeEnum, FileDirectoryDownloadResponse } from '@/types/download.types';
import { SensorConfiguration } from '@/types/sensor/configuration.types';
import { PerimeterCamera } from '@/types/sensor/sensors/perimeterCamera.types';

const downloadToastDetail: Record<DownloadToastTypeEnum, { status: ToastStatusEnum; messageId: string }> = {
  [DownloadToastTypeEnum.ERROR]: { status: ToastStatusEnum.ERROR, messageId: 'global.download.error' },
  [DownloadToastTypeEnum.SUCCESS]: { status: ToastStatusEnum.SUCCESS, messageId: 'global.download.success' },
  [DownloadToastTypeEnum.NO_RESULTS]: {
    status: ToastStatusEnum.ERROR,
    messageId: 'components.notificationSearch.export.noResults',
  },
};

export const createDownloadToast = (toastType: DownloadToastTypeEnum) => {
  const toast = downloadToastDetail[toastType];
  createToast(appIntl().formatMessage({ id: toast.messageId }), toast.status);
};

export const exportObjectInJson = (object: object, exportName: string) => {
  const dataStr = `data:text/json;charset=utf-8,${encodeURIComponent(JSON.stringify(object, null, 2))}`;
  const link = document.createElement('a');
  link.setAttribute('href', dataStr);
  link.setAttribute('download', `${exportName}.json`);
  document.body.appendChild(link);
  link.click();
  link.remove();

  createDownloadToast(DownloadToastTypeEnum.SUCCESS);
};

export const exportFileOrDirectory = (response: FileDirectoryDownloadResponse) => {
  if (response.error) {
    createDownloadToast(DownloadToastTypeEnum.ERROR);
    return;
  }

  const { blob, name, responseStatus } = response.data;
  if (responseStatus === 204) {
    createDownloadToast(DownloadToastTypeEnum.NO_RESULTS);
    return;
  }

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', name);
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);

  createDownloadToast(DownloadToastTypeEnum.SUCCESS);
};

export const importJsonFile = async <T extends PerimeterCamera | SensorConfiguration>(
  event: ChangeEvent<HTMLInputElement>,
  site: string,
  trigger: TypedMutationTrigger<void, { site: string; fileContent: T[] }, BaseQueryFn>,
  successLabel: string,
) => {
  const jsonFile = event.target.files?.length && event.target.files[0];
  if (jsonFile) {
    const stringContent = await jsonFile.text();
    const params = { site, fileContent: JSON.parse(stringContent) };

    trigger(params)
      .unwrap()
      .then(() => {
        createToast(appIntl().formatMessage({ id: successLabel }), ToastStatusEnum.SUCCESS);
      })
      .catch((error) => createToast(createErrorMessage(error), ToastStatusEnum.ERROR));
  }
};
